import { makeAutoObservable, runInAction } from 'mobx';
import { BACKEND_MODULE_TYPES, REPRESENTATIVE_ACTION_CONFIRMATION_TYPE } from 'components/common/constants';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from 'services/authUtils';
import {
  addRepresentativeRequest,
  removeRepresentativeRequest,
  updateRepresentativeRequest,
  getCandidateInfo,
  resendSecurityCode,
  forceChangeConfirmationTypeRequest,
  addConfirmationRepresentativeRequest,
  updateConfirmationRepresentativeRequest,
  removeConfirmationRepresentativeRequest
} from 'services/requestAgent';

class RepresentativeStore {
  isLoading = false;
  email = null;
  permissions = [];
  error = null;
  confirmationPopupError = null;
  isGetInfoSuccess = false;
  isAddRepresentativeSuccess = false;
  isUpdateRepresentativeSuccess = false;
  isDeleteRepresentativeSuccess = false;
  isShowingInternalActionConfirmation = false;
  isShowSwitchConfirmationMethodScreen = false;
  confirmationId = null;
  representativeActionConfirmationType = REPRESENTATIVE_ACTION_CONFIRMATION_TYPE.ADD;

  constructor() {
    makeAutoObservable(this);
  }

  resetStore() {
    this.isLoading = false;
    this.email = null;
    this.permissions = [];
    this.error = null;
    this.confirmationPopupError = null;
    this.isGetInfoSuccess = false;
    this.isAddRepresentativeSuccess = false;
    this.isUpdateRepresentativeSuccess = false;
    this.isDeleteRepresentativeSuccess = false;
    this.isShowingInternalActionConfirmation = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.confirmationId = null;
    this.representativeActionConfirmationType = REPRESENTATIVE_ACTION_CONFIRMATION_TYPE.ADD;
  }

  setIsLoading(status) {
    this.isLoading = status;
    this.error = null;
  }

  setIsShowingInternalActionConfirmation(status) {
    this.isShowingInternalActionConfirmation = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setRepresentativeActionConfirmationType(type) {
    this.representativeActionConfirmationType = type;
  }

  setEmail(email) {
    this.email = email;
  }

  clearConfirmationPopupError() {
    this.confirmationPopupError = null;
  }

  async getAccountInfo(email) {
    try {
      this.setIsLoading(true);
      const data = await getCandidateInfo(email);
      runInAction(() => {
        this.isLoading = false;
        this.isGetInfoSuccess = true;
      });
      return data;
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isLoading = false;
      });
      return null;
    }
  }

  async addNewRepresentative({ customerNumber, permissions }) {
    try {
      this.setIsLoading(true);

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await addRepresentativeRequest({
        customerNumber,
        email: this.email,
        permissions
      });

      setTimerDurationExpiresAt(expiresAt);
      updateConfirmationActionType(confirmationType);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  }

  async addNewRepresentativeConfirmation({ customerNumber, securityCode }) {
    try {
      this.setIsLoading(true);

      const data = await addConfirmationRepresentativeRequest({
        customerNumber,
        confirmationId: this.confirmationId,
        securityCode
      });
      runInAction(() => {
        this.isLoading = false;
        this.isShowingInternalActionConfirmation = false;
        this.isAddRepresentativeSuccess = true;
      });
      return data;
    } catch (err) {
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = err;
          this.error = err;
          this.isShowingInternalActionConfirmation = false;
          // Reset to representative form state
          this.confirmationId = null;
          this.error = { code: 'CONFIRMATION_REJECTED', message: 'Push notification was rejected. Please try again.' };
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.confirmationPopupError = err;
          this.error = err;
        }
        this.isLoading = false;
      });
      if (err?.code !== 'CONFIRMATION_PENDING_ACTION' && err?.code !== 'CONFIRMATION_REJECTED') {
        throw err;
      }
      // Return undefined for rejected or pending confirmations
      return undefined;
    }
  }

  async updateNewRepresentative({ customerNumber, id, permissions }) {
    try {
      this.setIsLoading(true);

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await updateRepresentativeRequest({
        customerNumber,
        id,
        permissions
      });

      setTimerDurationExpiresAt(expiresAt);
      updateConfirmationActionType(confirmationType);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  }

  async updateRepresentativeRequestConfirmation({ customerNumber, securityCode }) {
    try {
      this.setIsLoading(true);

      const data = await updateConfirmationRepresentativeRequest({
        customerNumber,
        confirmationId: this.confirmationId,
        securityCode
      });
      runInAction(() => {
        this.isLoading = false;
        this.isShowingInternalActionConfirmation = false;
        this.isUpdateRepresentativeSuccess = true;
      });
      return data;
    } catch (err) {
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = err;
          this.error = err;
          this.isShowingInternalActionConfirmation = false;
          // Reset to representative form state
          this.confirmationId = null;
          this.error = { code: 'CONFIRMATION_REJECTED', message: 'Push notification was rejected. Please try again.' };
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.confirmationPopupError = err;
          this.error = err;
        }
        this.isLoading = false;
      });
      if (err?.code !== 'CONFIRMATION_PENDING_ACTION' && err?.code !== 'CONFIRMATION_REJECTED') {
        throw err;
      }
      // Return undefined for rejected or pending confirmations
      return undefined;
    }
  }

  async removeNewRepresentative({ customerNumber, id }) {
    try {
      this.setIsLoading(true);

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await removeRepresentativeRequest({ customerNumber, id });

      setTimerDurationExpiresAt(expiresAt);
      updateConfirmationActionType(confirmationType);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.isShowingInternalActionConfirmation = true;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  }

  async removeRepresentativeRequestConfirmation({ customerNumber, securityCode }) {
    try {
      this.setIsLoading(true);

      const data = await removeConfirmationRepresentativeRequest({
        customerNumber,
        confirmationId: this.confirmationId,
        securityCode
      });
      runInAction(() => {
        this.isLoading = false;
        this.isShowingInternalActionConfirmation = false;
        this.isDeleteRepresentativeSuccess = true;
      });
      return data;
    } catch (err) {
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = err;
          this.error = err;
          this.isShowingInternalActionConfirmation = false;
          // Reset to representative form state
          this.confirmationId = null;
          this.error = { code: 'CONFIRMATION_REJECTED', message: 'Push notification was rejected. Please try again.' };
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.confirmationPopupError = err;
          this.error = err;
        }
        this.isLoading = false;
      });
      if (err?.code !== 'CONFIRMATION_PENDING_ACTION' && err?.code !== 'CONFIRMATION_REJECTED') {
        throw err;
      }
      // Return undefined for rejected or pending confirmations
      return undefined;
    }
  }

  async resendRepresentativeSecurityCode() {
    try {
      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.API, this.confirmationId);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(this.confirmationId, confirmationType, BACKEND_MODULE_TYPES.API);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };
}

export default new RepresentativeStore();
